import { Component, computed, inject, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { CarePaymentDialogStore } from './care-payment-dialog.store';
import { fb, routeParams } from 'packages/client/src/app/modules/shared/shared.global-service';
import { FilterBuilder } from 'packages/client/src/app/modules/content/helper/filter.builder';
import { Validators } from '@angular/forms';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { dateField } from 'packages/client/src/app/modules/shared/components/kit/table/table.fields';
import { TableColumn } from 'packages/client/src/app/modules/shared/components/kit/table/table.types';
import { TranslateService } from '@ngx-translate/core';
import { contentGlobalSuccess } from 'packages/client/src/app/modules/content/content.error';
import {
    CarePaymentCreateInput,
    CarePaymentUpdateInput,
} from '@libs/types/src/services/input-type/social/care-payment';
import { dateify } from '@libs/types/src/common/utils/date.utils';
import { CarePaymentObject } from '@libs/types/src/services/object-type/folder/care-payment.object';
import { SaveButtonComponent } from '../../../kit/button/save-button.component';
import { CancelButtonComponent } from '../../../kit/button/cancel-button.component';
import { BaseDialogComponent } from '../../../kit/base-dialog/base-dialog.component';
import { DateInputComponent } from '../../../kit/input/date-input/date-input.component';
import { ConfirmBuilder } from '../../../../modules/shared/service/confirm.builder';
import { FilterInput } from '@it2go/data-grid';
import { CardTableComponent } from '../../../../modules/shared/components/kit/table/card-table.component';
import { changeSignal } from '../../../../utils/form.utils';
import { CardComponent } from '../../../kit/card/card.component';
import { carePaymentFrag } from './care-payment-dialog.query';
import { PrimeTemplate } from '@openng/optimus-ui/api';

@Component({
    selector: 'app-care-payment-dialog',
    templateUrl: 'care-payment-dialog.component.html',
    styleUrl: 'care-payment-dialog.component.scss',
    providers: [CarePaymentDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DialogModule,
        SaveButtonComponent,
        CancelButtonComponent,
        DateInputComponent,
        CardComponent,
        CardTableComponent,
        PrimeTemplate,
    ],
})
export class CarePaymentDialogComponent extends BaseDialogComponent {

    protected readonly store = inject(CarePaymentDialogStore);
    public readonly confirm = inject(ConfirmBuilder);

    protected form = fb.group({
        id: fb.control<number | null>(null),
        activeFrom: fb.control<Date | null>(null, Validators.required),
        activeTo: fb.control<Date | null>(null, Validators.required),
    });

    protected prefix = 'folder.carePayment.';

    protected headerTitle = computed(() => {
        const groupId = this.formIdChanged();
        if (groupId) {
            return `${this.prefix}editPayment`;
        } else {
            return `${this.prefix}newPayment`;
        }
    });

    protected columns: TableColumn[] = [
        dateField('activeFrom'),
        dateField('activeTo'),
    ];

    protected additionalFilters = computed<FilterInput[][]>(() => {
        return FilterBuilder.singlePage({
            folderId: routeParams.folderId(),
        }).filter || [];
    });

    private paymentsTable = viewChild<CardTableComponent>(CardTableComponent);
    private formIdChanged = changeSignal<number | null>(this.form.controls.id);

    protected async submit() {
        if (this.form.isInvalid() || this.form.value.activeFrom! > this.form.value.activeTo!) return;

        const data: Record<string, any> = {
            activeFrom: this.form.value.activeFrom?.toISOString() || null,
            activeTo: this.form.value.activeTo?.toISOString() || null,
        };

        if (this.form.value.id) {
            data.id = this.form.value.id;
            this.store.api.updateCarePayment(data as CarePaymentUpdateInput, 'id').then(() => {
                this.form.reset();
                this.paymentsTable()?.reload();
                contentGlobalSuccess.next('common.assigned');
            });
        } else {
            data.folderId = Number(routeParams.folderId());
            this.store.api.createCarePayment(data as CarePaymentCreateInput, 'id').then(() => {
                this.form.reset();
                this.paymentsTable()?.reload();
                contentGlobalSuccess.next('common.created');
            });
        }
    }

    protected updateCarePayment(carePayment: CarePaymentObject) {
        this.form.patchValue({
            id: carePayment.id,
            activeFrom: dateify(carePayment.activeFrom),
            activeTo: dateify(carePayment.activeTo),
        });
    }

    protected deleteCarePayment(event: any) {
        this.confirm.confirmDelete(
            async () => {
                this.store.api.deleteCarePayment({ id: event.item.id }).then(() => {
                    this.paymentsTable()?.reload();
                    contentGlobalSuccess.next('common.deleted');
                });
            },
            event,
        );
    }

    protected readonly translate = inject(TranslateService);
    protected readonly carePaymentFrag = carePaymentFrag;
}
